'use client'

import { useCurrentUser } from '@/hooks/use-current-user'
import { cn } from "@/lib/utils"
import { formatDateInUserTimezone, formatTimeInUserTimezone } from '@/lib/utils/timezone'
import { Icon } from '@/components/ui/icon'
import { AppointmentStatus } from "@prisma/client"
import { motion } from "framer-motion"
import { useRouter } from "next/navigation"
import React, { useEffect, useState } from "react"

interface Appointment {
  id: string
  title: string
  scheduledAt: string
  status: AppointmentStatus
  serviceName?: string
  description?: string
  createdAt: string
}

interface AppointmentItemProps {
  id: string
  title: string
  scheduledAt: string
  status: AppointmentStatus
  isCompact?: boolean
}

const AppointmentItem: React.FC<AppointmentItemProps> = ({
  id,
  title,
  scheduledAt,
  status,
  isCompact
}) => {
  const router = useRouter()
  const user = useCurrentUser()

  // Match the status colors from client.tsx
  const statusColors = {
    PENDING: 'text-amber-800 bg-amber-100',
    CONFIRMED: 'text-green-800 bg-green-100',
    IN_PROGRESS: 'text-indigo-800 bg-indigo-100',
    COMPLETED: 'text-blue-800 bg-blue-100',
    CANCELLED: 'text-red-800 bg-red-100'
  }

  // Utility function to safely get color based on status
  const getStatusColor = (status: AppointmentStatus) => {
    return statusColors[status as keyof typeof statusColors] || 'text-gray-800 bg-gray-100';
  }

  const getStatusIcon = () => {
    switch (status) {
      case 'PENDING':
        return 'clock'
      case 'CONFIRMED':
        return 'check-circle-solar'
      case 'COMPLETED':
        return 'check-circle-solar'
      case 'CANCELLED':
        return 'close-circle-duotone'
      case 'IN_PROGRESS':
        return 'play-circle'
      default:
        return 'clock'
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    router.push(`/account?id=${id}`)
  }

  // Get user timezone preference, default to Edmonton
  const userTimezone = user?.userPreferences?.timezone || 'America/Edmonton'

  // Format date and time using user's timezone preference
  const formatDate = (dateString: string) => {
    try {
      if (!dateString) return "Invalid Date";
      return formatDateInUserTimezone(dateString, userTimezone, 'MMM D, YYYY');
    } catch (e) {
      console.error("Date formatting error:", e);
      return "Invalid Date";
    }
  };

  const formatTime = (dateString: string) => {
    try {
      if (!dateString) return "Invalid Time";
      return formatTimeInUserTimezone(dateString, userTimezone, 'h:mm A');
    } catch (e) {
      console.error("Time formatting error:", e);
      return "Invalid Time";
    }
  };

  const date = formatDate(scheduledAt);
  const time = formatTime(scheduledAt);

  if (isCompact) {
    return (
      <motion.a
        href={`/appointment/${id}`}
        onClick={handleClick}
        className="block w-full"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <div className="flex flex-col items-center mb-3">
          <div className="relative w-full aspect-square md:w-14 md:h-14 rounded-md flex items-center justify-center bg-background-100 overflow-hidden">
            <div className={cn("absolute inset-0 flex items-center justify-center", getStatusColor(status))}>
              <Icon icon={getStatusIcon()} className="w-6 h-6" />
            </div>
          </div>
          <span className="text-[10px] mt-1 text-center text-foreground/70 line-clamp-1 w-full">
            {time}
          </span>
        </div>
      </motion.a>
    )
  }

  return (
    <motion.a
      href={`/appointment/${id}`}
      onClick={handleClick}
      className="block w-full bg-background-50 rounded-md p-3 mb-2 hover:bg-background-100 transition-colors"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="flex justify-between items-start">
        <div>
          <h4 className="text-sm font-medium">{title}</h4>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex items-center gap-1">
              <Icon icon="book-calendar" className="w-3 h-3 text-foreground/60" />
              <span className="text-xs text-foreground/60">{date}</span>
            </div>
            <div className="flex items-center gap-1">
              <Icon icon="clock" className="w-3 h-3 text-foreground/60" />
              <span className="text-xs text-foreground/60">{time}</span>
            </div>
          </div>
        </div>
        <span className={cn("text-xs px-2 py-1 rounded-full", getStatusColor(status))}>
          {status.charAt(0) + status.slice(1).toLowerCase()}
        </span>
      </div>
    </motion.a>
  )
}

// Function to fetch appointments from the server
const fetchAppointments = async (): Promise<Appointment[]> => {
  try {
    const response = await fetch('/api/profile/appointments')

    // Check if response is not ok, but provide more detailed error information
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'No error details available');
      console.error(`API error (${response.status}): ${errorText}`);
      throw new Error(`Failed to fetch appointments: ${response.status} ${response.statusText}`);
    }

    const data = await response.json()
    return data.appointments || []
  } catch (error) {
    console.error('Error fetching appointments:', error)
    // Don't throw the error further, just return empty array to prevent component crash
    return []
  }
}

export const PendingAppointmentsSection: React.FC<{ isCompact?: boolean }> = ({ isCompact = false }) => {
  const [appointments, setAppointments] = useState<Appointment[]>([])
  const [loading, setLoading] = useState(true)

  // Fetch appointments on component mount
  useEffect(() => {
    const getAppointments = async (retryCount = 0) => {
      setLoading(true)
      try {
        const data = await fetchAppointments()
        setAppointments(data)
        setLoading(false)
      } catch (error) {
        console.error('Failed to load appointments:', error)

        // Add retry logic (up to 2 retries) with exponential backoff
        if (retryCount < 2) {
          console.log(`Retrying appointment fetch (attempt ${retryCount + 1}/2)...`)
          setTimeout(() => {
            getAppointments(retryCount + 1)
          }, 1000 * Math.pow(2, retryCount)) // 1s, then 2s delay
        } else {
          // After all retries, still show empty state
          setAppointments([])
          setLoading(false)
        }
      }
    }

    getAppointments()

    // Set up a polling interval to refresh appointments every 5 minutes
    const interval = setInterval(() => getAppointments(), 5 * 60 * 1000)

    return () => clearInterval(interval)
  }, [])

  // View all appointments link
  const viewAllLink = '/account'

  if (loading) {
    return (
      <div className={cn(
        "w-full flex justify-center items-center py-4",
        isCompact ? "px-2" : "px-3"
      )}>
        <div className="animate-pulse flex space-x-2 items-center">
          <div className="rounded-full bg-background-200 h-4 w-4"></div>
          <div className="h-2 w-24 bg-background-200 rounded"></div>
        </div>
      </div>
    )
  }

  if (appointments.length === 0) {
    return null
  }

  // Filter appointments to show only active ones
  const upcomingAppointments = appointments.filter(
    app => ['PENDING', 'CONFIRMED', 'IN_PROGRESS'].includes(app.status as string)
  )

  if (upcomingAppointments.length === 0) {
    return null
  }

  if (isCompact) {
    return (
      <div className="flex flex-col items-center pt-2 pb-4">
        <div className="flex items-center justify-center mb-2">
          <Icon icon="calendar-mark" className="w-4 h-4 text-primary-500" />
          <span className="text-[8px] ml-1 px-1.5 py-0.5 bg-background-200 rounded-full text-foreground/80">
            {upcomingAppointments.length}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-y-2 w-full items-center px-1">
          {upcomingAppointments.slice(0, 3).map((appointment) => (
            <AppointmentItem
              key={appointment.id}
              id={appointment.id}
              title={appointment.title}
              scheduledAt={appointment.scheduledAt}
              status={appointment.status}
              isCompact={true}
            />
          ))}

          {upcomingAppointments.length > 3 && (
            <motion.a
              href={viewAllLink}
              className="text-[10px] text-center mt-2 text-primary-500 hover:text-primary-600 font-medium"
              whileHover={{ scale: 1.05 }}
            >
              View all ({upcomingAppointments.length})
            </motion.a>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="w-full px-3 py-3 sm:px-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <Icon icon="calendar-mark" className="w-5 h-5 text-primary-500 mr-2" />
          <h3 className="text-sm font-medium">Upcoming Appointments</h3>
        </div>
        <a
          href={viewAllLink}
          className="text-xs px-2 py-1 bg-background-200 rounded-full text-foreground/80 hover:bg-background-300 transition-colors"
        >
          {upcomingAppointments.length}
        </a>
      </div>

      <div className="space-y-2">
        {upcomingAppointments.slice(0, 5).map((appointment) => (
          <AppointmentItem
            key={appointment.id}
            id={appointment.id}
            title={appointment.title}
            scheduledAt={appointment.scheduledAt}
            status={appointment.status}
          />
        ))}

        {upcomingAppointments.length > 5 && (
          <a
            href={viewAllLink}
            className="block w-full text-center py-2 text-xs text-primary-500 hover:text-primary-600 font-medium"
          >
            View all {upcomingAppointments.length} appointments
          </a>
        )}
      </div>
    </div>
  )
}
